import { CheckCircle2, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { getStepTitle } from "./SprintRoadmap";

/**
 * Compact horizontal stepper for mobile / narrow tablet (< ~960px).
 * Keeps the operational step above the fold by replacing the tall
 * vertical roadmap list with a sticky 5-pill strip.
 */
export default function SprintStepperCompact({
  completed,
  current,
  active,
  visited = [],
  onSelect,
  readOnly,
  runningStep = null,
}: {
  completed: number[];
  current: number;
  active: number;
  visited?: number[];
  onSelect: (n: number) => void;
  readOnly: boolean;
  runningStep?: number | null;
}) {
  const activeTitle = getStepTitle(active);

  return (
    <div className="sticky top-2 z-20 rounded-xl border border-white/10 bg-slate-950/85 backdrop-blur-md px-3 py-2.5 shadow-lg shadow-black/20">
      <div className="flex items-center justify-between gap-3 mb-2">
        <p className="text-[11px] uppercase tracking-wider text-secondary font-medium shrink-0">
          Step {active} di 5
        </p>
        <p className="text-[11px] text-slate-300 truncate">{activeTitle}</p>
      </div>
      <div
        role="tablist"
        aria-label="Navigazione Sprint"
        className="flex items-center gap-1.5"
      >
        {[1, 2, 3, 4, 5].map((n, idx) => {
          const isDone = completed.includes(n);
          const isCurrent = !isDone && n === current;
          const isVisited = visited.includes(n);
          const isLocked = !isDone && !isCurrent && !isVisited;
          const isRunning = !isDone && n === runningStep;
          const clickable = readOnly ? isDone : isDone || isCurrent || isVisited;
          const isActive = n === active;
          const title = getStepTitle(n);
          const stateLabel = isDone
            ? "completato"
            : isRunning
              ? "in esecuzione"
              : isCurrent
                ? "da fare ora"
                : "bloccato";

          return (
            <div key={n} className="flex items-center flex-1 min-w-0">
              <button
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-label={`Step ${n}: ${title} — ${stateLabel}`}
                title={
                  clickable
                    ? `Step ${n} · ${title}`
                    : isLocked
                      ? `Completa prima lo Step ${current} per sbloccare`
                      : title
                }
                disabled={!clickable}
                onClick={() => clickable && onSelect(n)}
                className={cn(
                  "flex-1 inline-flex items-center justify-center gap-1 h-9 rounded-lg text-xs font-semibold border transition-all",
                  isActive
                    ? "border-primary/60 bg-primary/15 text-primary-foreground ring-1 ring-primary/40"
                    : isDone
                      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
                      : isRunning
                        ? "border-amber-500/40 bg-amber-500/10 text-amber-300 animate-pulse"
                        : isCurrent
                          ? "border-primary/40 bg-primary/10 text-primary"
                          : "border-white/10 bg-white/[0.03] text-slate-500",
                  !clickable && "cursor-not-allowed opacity-70",
                  clickable && !isActive && "hover:bg-white/[0.06]"
                )}
              >
                {isDone ? (
                  <CheckCircle2 className="h-3.5 w-3.5" />
                ) : isLocked && !isRunning ? (
                  <Lock className="h-3 w-3" />
                ) : null}
                <span>{n}</span>
              </button>

              {idx < 4 && (
                <span
                  aria-hidden
                  className={cn(
                    "h-px w-1.5 mx-0.5",
                    completed.includes(n) ? "bg-emerald-500/40" : "bg-white/10"
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
